import {getMetaKeyLabel, TooltipForChild} from "@cdx/common";
import {joinByBr} from "../../lib/utils";
import {
  Col,
  DSIconAttachment,
  DSIconButton,
  DSIconCheck,
  DSIconConversation,
  DSIconDoc,
  DSIconPlay,
  DSIconPlus,
  DSIconProperties,
  Row,
} from "@cdx/ds";
import {SetActions} from "../../lib/hooks/useSet";
import {ReactNode} from "react";

type FilterType = (typeof filterTypes)[number]["type"];

export type FeedFilterType = FilterType;

type FilterButtonProps = {
  icon: ReactNode;
  tooltip: string | ReactNode;
  type: FilterType;
  activeFilters: Set<FilterType>;
  activeFiltersActions: SetActions<FilterType>;
};

const FilterButton = ({
  icon,
  tooltip,
  type,
  activeFilters,
  activeFiltersActions,
}: FilterButtonProps) => {
  const isActive = activeFilters.has(type);
  const handleClick = () => activeFiltersActions.toggle(type);
  const handleMetaClick = () =>
    isActive ? activeFiltersActions.toggle(type) : activeFiltersActions.set(new Set([type]));
  return (
    <TooltipForChild
      tooltip={
        <Col px="8px" py="4px">
          {tooltip}
        </Col>
      }
    >
      <DSIconButton
        active={isActive}
        onClick={handleClick}
        onMetaClick={handleMetaClick}
        icon={icon}
        variant="secondary"
        size="sm"
      />
    </TooltipForChild>
  );
};

const filterTypes = [
  {
    type: "created",
    icon: <DSIconPlus />,
    tooltip: "Show created cards",
    constraint: {
      type: "card_update",
      data: {op: "jsonSearch", field: "version", jsonOp: "eq", value: 1},
    },
  },
  {
    type: "status",
    icon: <DSIconPlay />,
    tooltip: "Show workflow state changes",
    constraint: {
      type: "card_update",
      $and: [
        {data: {op: "jsonSearch", field: "version", jsonOp: "neq", value: 1}},
        {
          $or: [
            {data: {op: "jsonSearch", field: "diff.visibility", jsonOp: "exists"}},
            {
              $and: [
                {data: {op: "jsonSearch", field: "diff.status", jsonOp: "exists"}},
                {data: {op: "jsonSearch", field: "diff.status.1", jsonOp: "neq", value: "done"}},
              ],
            },
          ],
        },
      ],
    },
  },
  {
    type: "done",
    icon: <DSIconCheck />,
    tooltip: "Show done cards",
    constraint: {
      type: "card_update",
      $and: [
        {data: {op: "jsonSearch", field: "diff.status.1", jsonOp: "eq", value: "done"}},
        {data: {op: "jsonSearch", field: "version", jsonOp: "neq", value: 1}},
      ],
    },
  },
  {
    type: "attachments",
    icon: <DSIconAttachment />,
    tooltip: "Show attachment changes",
    constraint: {
      type: "card_update",
      data: {op: "jsonSearch", field: "diff.attachments", jsonOp: "exists"},
    },
  },
  {
    type: "props",
    icon: <DSIconProperties />,
    tooltip: (
      <>
        Show property changes
        <br />
        (effort, priority, owner, milestone)
      </>
    ),
    constraint: {
      type: "card_update",
      $and: [
        {
          $or: [
            {data: {op: "jsonSearch", field: "diff.priority", jsonOp: "exists"}},
            {data: {op: "jsonSearch", field: "diff.effort", jsonOp: "exists"}},
            {data: {op: "jsonSearch", field: "diff.assigneeId", jsonOp: "exists"}},
            {data: {op: "jsonSearch", field: "diff.milestoneId", jsonOp: "exists"}},
            {data: {op: "jsonSearch", field: "diff.dueDate", jsonOp: "exists"}},
          ],
        },
        {data: {op: "jsonSearch", field: "version", jsonOp: "neq", value: 1}},
      ],
    },
  },
  {
    type: "content",
    icon: <DSIconDoc />,
    tooltip: "Show content changes",
    constraint: {
      type: "card_update",
      $and: [
        {data: {op: "jsonSearch", field: "diff.content", jsonOp: "exists"}},
        {data: {op: "jsonSearch", field: "version", jsonOp: "neq", value: 1}},
      ],
    },
  },
  {
    type: "conversations",
    icon: <DSIconConversation />,
    tooltip: "Show conversations",
    constraint: {type: "comment"},
  },
] as const;

export const filterTypeToContraint = filterTypes.reduce(
  (m, {type, constraint}) => {
    m[type] = constraint;
    return m;
  },
  {} as Record<FilterType, any>
);

type ActivityFilterButtonsProps = {
  activeFilters: Set<FilterType>;
  activeFiltersActions: SetActions<FilterType>;
};
export const ActivityFilterButtons = (props: ActivityFilterButtonsProps) => {
  const {activeFilters, activeFiltersActions} = props;
  return (
    <Row sp="8px">
      {filterTypes.map(({type, tooltip, icon}) => (
        <FilterButton
          tooltip={joinByBr(
            [
              tooltip,
              activeFilters.size > 0 &&
                !activeFilters.has(type) &&
                `Use ${getMetaKeyLabel()}+click to select this option only.`,
            ].filter(Boolean)
          )}
          key={type}
          type={type}
          icon={icon}
          activeFilters={activeFilters}
          activeFiltersActions={activeFiltersActions}
        />
      ))}
    </Row>
  );
};
